<template>
  <RenderComponents
    v-if="sharedNodeSchema"
    :schema="sharedNodeSchema"
    :ComponentRegistry="ComponentRegistry"
    :editable="false"
    :shared-node-internal="true"
    @update:schema-fields="(id, val) => emit('update:schema-fields', id, val)"
  />
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, provide, type ComputedRef } from 'vue'
import type { ComponentNode, ComponentSchema } from '../api/types.gen'
import { PipeDisplayMode, usePipe } from './composables/usePipe'
import { usePipeEventHandlers } from './composables/usePipeEventHandlers'
import RenderComponents from './RenderComponents.vue'

const props = defineProps<{
  node: ComponentNode
  ComponentRegistry: Record<string, { component: unknown }>
  // Accepted for API consistency with the tree; the shared template is always rendered
  // readonly (we pass :editable="false" downward), regardless of this prop's value.
  editable?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:schema-fields', nodeId: string, value: ComponentNode['fields']): void
}>()

const pipe = usePipe()
const { getEventListeners } = usePipeEventHandlers(pipe)

// Build listeners for the SharedNodeRef's own events (processors attached per-instance).
// Source key identifies THIS ref instance so component_processor resolution
// walks the correct prefix.
const refListeners = computed(() => getEventListeners(props.node.events || {}, props.node.nodeId))

// Subscribe to navigation broadcasts while mounted, and dispatch onPageShow /
// onPageLeave on the referenced SharedNode's own events. Subscribe-based (not
// lifecycle-based) on purpose: a shared component being v-if-toggled inside a
// page should NOT fire page events — only real route transitions should.
let unsubShow: (() => void) | null = null
let unsubLeave: (() => void) | null = null
let unsubExitIntent: (() => void) | null = null

onMounted(() => {
  if (pipe.currentMode.value === PipeDisplayMode.EDITOR) return

  const runEvent = (eventName: 'onPageShow' | 'onPageLeave' | 'onExitIntent', payload: unknown) => {
    const snId = props.node.fields?.sharedNodeId as string | undefined
    if (!snId) return
    const sn = pipe.getSharedNodeById(snId)
    const events = sn?.events
    if (!events) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getEventListeners(events as any, props.node.nodeId)[eventName]?.(payload)
  }

  unsubShow = pipe.subscribe('navigation:show', (p) => runEvent('onPageShow', p))
  unsubLeave = pipe.subscribe('navigation:leave', (p) => runEvent('onPageLeave', p))
  unsubExitIntent = pipe.subscribe('navigation:exit-intent', (p) => runEvent('onExitIntent', p))
})

onBeforeUnmount(() => {
  unsubShow?.()
  unsubLeave?.()
  unsubExitIntent?.()
})

// Provide a reemit function so inner components with 'reemit' processors can bubble events up.
// Returns the listener's Promise so awaiting callers see the full parent processor chain
// finish before they continue.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
provide('reemitHandler', async (eventName: string, ...args: any[]) => {
  const handler = refListeners.value[eventName]
  if (handler) return handler(...args)
})

// Build slot filler map from this ref's children, keyed by slot name
const slotsMap = computed<Record<string, ComponentNode[]>>(() => {
  const map: Record<string, ComponentNode[]> = {}
  for (const child of props.node.children ?? []) {
    const slotName = child.slot || 'default'
    if (!map[slotName]) map[slotName] = []
    map[slotName].push(child)
  }
  return map
})

// Provide slot fillers to nested ComponentSlot nodes
provide('sharedNodeSlots', slotsMap)

// Expose the SharedNodeRef's nodeId so ComponentSlot drop targets can reference it
provide('sharedNodeRefId', props.node.nodeId)

// Ancestry chain for component-processor instance keys. Scalar `sharedNodeRefId`
// above is intentionally left as-is (overwrites on nesting — consumed by drop
// targets that only care about the NEAREST ref). This separate array key
// accumulates across nested SharedNodeRefs so `useComponentProcessors` can
// build a unique `instanceKey` per live instance of a shared template.
//
// MUST be a computed (not a one-shot array): when navigating between pages
// that embed the same shared template, its `siblingRenderKeys` key reuses
// this component instance with new `props.node`. A static array would stay
// bound to the original SNR's nodeId; injectors downstream would build
// stale instance keys and component_processor lookups would miss.
const parentAncestry = inject<ComputedRef<readonly string[]>>(
  'sharedNodeRefAncestry',
  computed(() => []),
)
const ancestry = computed(() => [...parentAncestry.value, props.node.nodeId])
provide('sharedNodeRefAncestry', ancestry)

// Editability handoff for slot fillers: the shared template subtree is forced
// readonly via `:editable="false"` below, but ComponentSlot fillers are user
// content placed onto this instance — they should follow the editability THIS
// ref received from its own parent. RenderComponents' ComponentSlot filler
// branch reads this injection. Nested refs propagate naturally: a SNR rendered
// inside an outer ref's template inherits `editable=false` and provides
// `outerEditable=false`, so ITS slot fillers are correctly readonly too.
provide(
  'outerEditable',
  computed(() => !!props.editable),
)

const sharedNodeSchema = computed<ComponentSchema | null>(() => {
  const snId = props.node.fields?.sharedNodeId as string | undefined
  if (!snId) return null
  const sn = pipe.getSharedNodeById(snId)
  return sn ? (sn.content as ComponentSchema) : null
})
</script>
